import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { LogOut, Download, ShieldCheck } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ProsperLogo } from "../components/ProsperLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginView = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    try {
      setLoading(true);
      await login(email, password);
      toast.success("Welcome back");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#F4FBFF] via-white to-[#F6F2FF] px-6">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-md rounded-3xl bg-white ring-1 ring-slate-100 shadow-xl p-8 sm:p-10">
        <Link to="/" className="flex justify-center">
          <ProsperLogo size="sm" />
        </Link>
        <div className="mt-8 text-center">
          <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-[#E6FBF6]">
            <ShieldCheck className="h-5 w-5 text-[#0FB5A6]" />
          </div>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight">Admin sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Access captured signups and contact messages.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <Input
            data-testid="admin-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-xl border-slate-200 bg-white px-5 text-base"
            autoComplete="email"
          />
          <Input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 rounded-xl border-slate-200 bg-white px-5 text-base"
            autoComplete="current-password"
          />
          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-xl text-base font-semibold text-white"
            style={{ background: "linear-gradient(90deg, #22B6B0 0%, #4A7AF0 55%, #7A5BFF 100%)" }}
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-2xl ring-1 ring-slate-100 bg-white p-5 shadow-sm">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="mt-2 text-3xl font-extrabold tracking-tight">{value}</p>
  </div>
);

const downloadCsv = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [signups, setSignups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("signups");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, m, st] = await Promise.all([
          axios.get(`${API}/admin/signups`),
          axios.get(`${API}/admin/contact-messages`),
          axios.get(`${API}/admin/stats`),
        ]);
        if (!alive) return;
        setSignups(s.data || []);
        setMessages(m.data || []);
        setStats(st.data || null);
      } catch (e) {
        toast.error("Failed to load data");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = signups.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (r.email || "").toLowerCase().includes(q) ||
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.referral_source || "").toLowerCase().includes(q) ||
      (r.source || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <ProsperLogo size="sm" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <Button
              data-testid="admin-logout-button"
              variant="outline"
              onClick={logout}
              className="h-9 rounded-lg"
            >
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Admin Dashboard</h1>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Total signups" value={stats?.total_signups ?? "—"} />
          <Stat label="Contact messages" value={stats?.total_contact_messages ?? "—"} />
          <Stat label="Top channel" value={(() => {
            const m = stats?.by_referral_source || {};
            const entries = Object.entries(m).filter(([k]) => k !== "(none)");
            if (!entries.length) return "—";
            entries.sort((a, b) => b[1] - a[1]);
            return entries[0][0];
          })()} />
        </div>

        {/* Channel breakdown */}
        {stats && Object.keys(stats.by_referral_source || {}).length > 0 && (
          <div className="mt-6 rounded-2xl bg-white ring-1 ring-slate-100 p-5">
            <p className="text-sm font-semibold text-slate-700">Acquisition channels</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(stats.by_referral_source).map(([k, v]) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                >
                  <span>{k}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-slate-900">{v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 border-b border-slate-200">
          {[
            { key: "signups", label: `Signups (${signups.length})` },
            { key: "messages", label: `Messages (${messages.length})` },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#22B6B0] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "signups" ? (
          <div className="mt-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Input
                data-testid="admin-search-input"
                placeholder="Search by name, email, channel…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-10 max-w-md rounded-lg border-slate-200 bg-white"
              />
              <Button
                data-testid="admin-download-csv-button"
                variant="outline"
                onClick={() => downloadCsv(filtered, "prosper-signups.csv")}
                className="h-10 rounded-lg"
              >
                <Download className="h-4 w-4 mr-2" /> Download CSV
              </Button>
            </div>

            <div className="mt-4 rounded-2xl bg-white ring-1 ring-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Source</th>
                      <th className="px-4 py-3 text-left font-semibold">How heard</th>
                      <th className="px-4 py-3 text-left font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No signups yet</td></tr>
                    ) : (
                      filtered.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">{r.full_name || "—"}</td>
                          <td className="px-4 py-3">{r.email}</td>
                          <td className="px-4 py-3">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{r.source}</span>
                          </td>
                          <td className="px-4 py-3">{r.referral_source || "—"}</td>
                          <td className="px-4 py-3 text-slate-500">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl bg-white ring-1 ring-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Page</th>
                    <th className="px-4 py-3 text-left font-semibold">Message</th>
                    <th className="px-4 py-3 text-left font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
                  ) : messages.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No messages yet</td></tr>
                  ) : (
                    messages.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50 align-top">
                        <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{r.email}</td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{r.page}</span>
                        </td>
                        <td className="px-4 py-3 max-w-md whitespace-pre-wrap">{r.message}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Admin = () => {
  const { user, checking } = useAuth();
  if (checking) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  }
  if (!user) return <LoginView />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Dashboard />;
};

export default Admin;
